import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { Check } from 'lucide-react';

const benefits = [
  { n: '10%', label: 'Off every order', detail: 'Automatically applied at checkout on every subscription cycle.' },
  { n: 'Free', label: 'USA shipping', detail: 'No minimum. Every subscription order ships free within the United States.' },
  { n: 'Zero', label: 'Commitment', detail: 'Skip, pause, or cancel at any time from your account. No fees, no questions.' },
  { n: 'First', label: 'Access', detail: 'Subscribers get early access to new formulas and seasonal releases before anyone else.' },
];

const plans = [
  { id: 'monthly', label: 'Monthly', sublabel: 'Delivered every 30 days', popular: true },
  { id: 'bimonthly', label: 'Every 2 Months', sublabel: 'Delivered every 60 days', popular: false },
  { id: 'quarterly', label: 'Quarterly', sublabel: 'Delivered every 90 days', popular: false },
];

const steps = [
  { n: '01', title: 'Choose your product', body: 'Go to any product page and select "Subscribe & Save" at checkout to activate the 10% discount.' },
  { n: '02', title: 'Set your frequency', body: 'Pick monthly, every two months, or quarterly — whatever fits your routine.' },
  { n: '03', title: 'Relax and restock', body: 'Your order ships automatically on schedule. Manage everything from your account dashboard.' },
];

export function SubscribePage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const navigate = useNavigate();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-32 pb-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-end">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Subscribe & Save
              </p>
              <h1 className="text-5xl md:text-6xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                Never run out.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>Always save 10%.</span>
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.5)', maxWidth: '44ch' }}>
                Subscribe to any Berleto product and get 10% off every order, free USA shipping, and the freedom to pause or cancel whenever you like.
              </p>
            </motion.div>

            {/* Key number */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="border-l pl-16"
              style={{ borderColor: 'rgba(26,42,26,0.08)' }}
            >
              <p className="text-[80px] md:text-[100px] font-light leading-none mb-2" style={{ color: '#1A2A1A' }}>10%</p>
              <p className="text-xs tracking-widest uppercase" style={{ color: 'rgba(26,42,26,0.4)' }}>
                Off every subscription order
              </p>
              <p className="text-xs mt-4" style={{ color: 'rgba(26,42,26,0.35)' }}>
                Bundle with all 3 products? Save up to 25%.
              </p>
              <button
                onClick={() => navigate('/bundle')}
                className="mt-3 text-xs transition-colors"
                style={{ color: '#B8704D' }}
                onMouseEnter={e => e.currentTarget.style.color = '#a56340'}
                onMouseLeave={e => e.currentTarget.style.color = '#B8704D'}
              >
                View bundle deal →
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="px-8 py-10"
              >
                <p className="text-3xl font-light mb-1" style={{ color: '#1A2A1A' }}>{b.n}</p>
                <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#B8704D' }}>{b.label}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(26,42,26,0.5)' }}>{b.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-14"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Delivery schedule
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#1A2A1A' }}>
              Choose your frequency.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-0 border" style={{ borderColor: 'rgba(26,42,26,0.12)' }}>
            {plans.map((plan, i) => (
              <motion.button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative text-left px-8 py-10 transition-colors border-r last:border-r-0`}
                style={{
                  borderColor: 'rgba(26,42,26,0.12)',
                  background: selectedPlan === plan.id ? '#1A2A1A' : 'transparent',
                }}
              >
                {plan.popular && (
                  <span
                    className="absolute top-6 right-6 text-[10px] tracking-widest uppercase px-2 py-0.5"
                    style={{ background: '#B8704D', color: '#FAF9F6' }}
                  >
                    Popular
                  </span>
                )}
                <p
                  className="text-base font-medium mb-2"
                  style={{ color: selectedPlan === plan.id ? '#FAF9F6' : '#1A2A1A' }}
                >
                  {plan.label}
                </p>
                <p
                  className="text-xs mb-6"
                  style={{ color: selectedPlan === plan.id ? 'rgba(232,220,196,0.5)' : 'rgba(26,42,26,0.45)' }}
                >
                  {plan.sublabel}
                </p>
                <p
                  className="text-2xl font-light"
                  style={{ color: selectedPlan === plan.id ? '#B8704D' : 'rgba(26,42,26,0.3)' }}
                >
                  10% off
                </p>
                {selectedPlan === plan.id && (
                  <div className="absolute bottom-6 right-6">
                    <Check className="w-4 h-4" style={{ color: '#B8704D' }} />
                  </div>
                )}
              </motion.button>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={() => navigate('/products')}
              className="px-10 py-4 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#243424'}
              onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
            >
              Start Subscribing
            </button>
            <p className="text-xs" style={{ color: 'rgba(26,42,26,0.45)' }}>
              Select "Subscribe & Save" on any product page. Cancel or change anytime.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="md:sticky md:top-32"
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                How it works
              </p>
              <h2 className="text-4xl md:text-5xl font-light leading-tight" style={{ color: '#1A2A1A' }}>
                Three steps.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>That's it.</span>
              </h2>
            </motion.div>

            <div>
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-6 py-8 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <span className="text-xs font-medium tracking-widest mt-0.5 flex-shrink-0 w-6" style={{ color: '#B8704D' }}>
                    {step.n}
                  </span>
                  <div>
                    <h3 className="text-base font-medium mb-2" style={{ color: '#1A2A1A' }}>{step.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)' }}>{step.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dark CTA */}
      <section className="py-24" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Ready to start
              </p>
              <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ color: '#FAF9F6' }}>
                Your stack.<br />
                <span style={{ color: 'rgba(232,220,196,0.4)' }}>On autopilot.</span>
              </h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/products')}
                  className="px-10 py-4 text-sm font-medium tracking-wide transition-colors"
                  style={{ background: '#FAF9F6', color: '#1A2A1A' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#E8DCC4'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FAF9F6'}
                >
                  Shop Products
                </button>
                <button
                  onClick={() => navigate('/bundle')}
                  className="px-10 py-4 text-sm font-medium tracking-wide transition-colors border"
                  style={{ borderColor: 'rgba(232,220,196,0.2)', color: 'rgba(232,220,196,0.7)', background: 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#FAF9F6'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(232,220,196,0.7)'}
                >
                  View Bundle (Save 25%)
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="border-t"
              style={{ borderColor: 'rgba(139,154,139,0.15)' }}
            >
              {[
                { label: 'Individual subscription', value: '10% off every order' },
                { label: 'Bundle subscription', value: 'Up to 25% off' },
                { label: 'Shipping', value: 'Free within USA' },
                { label: 'Cancel policy', value: 'Anytime, no fees' },
                { label: 'Charge date', value: 'Same day each month' },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center py-5 border-b"
                  style={{ borderColor: 'rgba(139,154,139,0.15)' }}
                >
                  <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(139,154,139,0.5)' }}>{row.label}</span>
                  <span className="text-sm" style={{ color: '#FAF9F6' }}>{row.value}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
